import axios from 'axios';
import { logger } from '@/lib/logger';
import type { ProfileWaiting } from '@/types/appDashboard';
import { shouldFetchAnotherUsersSimplePage } from '@/utils/usersSimplePagination';

/** Page size used by fetch-all pagination requests. */
export const USERS_SIMPLE_FETCH_PAGE_SIZE = 100;

/** Hard cap on fetch-all pagination requests (100 pages × 100 users = 10,000 users max). */
export const MAX_PAGINATED_FETCH_PAGES = 100;

/** Maximum users that can be loaded via fetch-all before pagination stops. */
export const MAX_USERS_FETCH_ALL_COUNT = USERS_SIMPLE_FETCH_PAGE_SIZE * MAX_PAGINATED_FETCH_PAGES;

/** Show a performance warning in Hub when fetch-all may load at least this many users. */
export const LARGE_USERS_DATASET_WARNING_THRESHOLD = 500;

const apiUrl = 'users/users-simple';

export interface UserSimpleResponse {
  Username: string;
  uuid: string;
  email: string;
  UserCreateDate: string;
  UserStatus: 'CONFIRMED' | 'UNCONFIRMED';
  Enabled: boolean;
  Profile: UserProfileData | null;
}

interface UserProfileData {
  user_profile_id: string;
  role:
    | 'SUPER_ADMIN'
    | 'DOCTOR'
    | 'ADMIN'
    | 'CLIENT'
    | 'CORPORATE_ADMIN'
    | 'BUSINESS_DEVELOPMENT'
    | 'READ_ONLY'
    | 'OPERATIONS_SUPPORT'
    | 'CLINICAL_SUPPORT'
    | 'NUTRITIONIST'
    | 'PHARMACIST'
    | 'SUPER_DOCTOR';
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  phone_country_code: string;
  date_of_birth: string;
  gender: string | null;
  occupation: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zipcode: string | null;
  emergency_contact_name: string | null;
  emergency_contact_relation: string | null;
  emergency_contact_number: string | null;
  client_id: string | null;
  created_at: string;
  updated_at: string;
  /** Active program display names. Populated by GET users/users-simple for Hub client lists. */
  program_names?: string[];
  /** Clinical pod assignments. Populated by GET users/users-simple for Hub pod filters. */
  pods?: Array<{ pod_id: string; name?: string }>;
  /** Tag assignments. Populated by GET users/users-simple for Hub tag filters. */
  tags?: Array<{ tag_id: string; name?: string }>;
  /** Group memberships. Populated by GET users/users-simple for Hub group filters. */
  groups?: Array<{ group_id: string; name?: string }>;
  /**
   * Waiting-days clock after Thyrocare + Thrive Check (cognitive optional).
   * Populated by GET users/users-simple for the App Dashboard.
   */
  profile_waiting?: ProfileWaiting | null;
  /**
   * Doctor's Notes list status. `complete` when a consultation summary PDF
   * exists, visit summary text exists, or the note has been shared.
   */
  doctors_note?: {
    status: 'complete' | 'pending' | 'not_started';
    shared: boolean;
    has_summary: boolean;
  } | null;
  // Nested by the backend via select_related("profile__app_state"). Omitted for
  // legacy profiles created before the app-state signal — treat as defaults.
  app_state?: {
    app_state_id: string;
    is_app_unlocked: boolean;
    app_unlocked_at: string | null;
    app_unlocked_by: string | null;
    is_onboarded: boolean;
    onboarded_at: string | null;
    theme_mode: string;
  } | null;
}

interface UserListResponse {
  count: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  pinned?: UserSimpleResponse[];
  results: UserSimpleResponse[];
  /** True when fetch-all stopped before all matching users were retrieved. */
  truncated?: boolean;
  /** Upper bound on users fetch-all can load (see MAX_USERS_FETCH_ALL_COUNT). */
  max_fetchable_users?: number;
}

interface UserListParams {
  role?:
    | 'SUPER_ADMIN'
    | 'DOCTOR'
    | 'ADMIN'
    | 'CLIENT'
    | 'CORPORATE_ADMIN'
    | 'BUSINESS_DEVELOPMENT'
    | 'READ_ONLY'
    | 'OPERATIONS_SUPPORT'
    | 'CLINICAL_SUPPORT'
    | 'NUTRITIONIST'
    | 'PHARMACIST'
    | 'SUPER_DOCTOR';
  email?: string;
  page?: number;
  page_size?: number;
  search?: string;
  search_by?: 'all' | 'name' | 'email' | 'client_id' | 'phone';
  pinned_ids?: string;
  /** Server-side Hub filters (GET users/users-simple). */
  city?: string;
  gender?: string;
  program?: string;
  /** Clinical pod UUID (`pod_id`). Absence sentinel `no_pods` is Hub-only and must not be sent. */
  pod?: string;
  tag?: string;
  group?: string;
  /**
   * Server sort. Hub sends `name` (default), `-created_at`, `client_id`, or
   * `-client_id`. App Dashboard uses `waiting_days` / `-waiting_days`
   * (`-waiting_days` is longest wait first; clients not yet waiting sort last).
   */
  ordering?: string;
  /** App Dashboard: `true` unlocked, `false` locked (including missing app-state). */
  is_app_unlocked?: 'true' | 'false';
  /** App Dashboard: `true` has an active enrollment, `false` does not. */
  enrollment_active?: 'true' | 'false';
  /** App Dashboard outstanding-action chip. Applied before pagination. */
  pending?: 'doctors_note' | 'nutrition_plan' | 'pinned_biomarkers' | 'app_unlock';
}

export async function getUsersSimple(
  params?: Omit<UserListParams, 'page' | 'page_size'>,
  accessToken?: string
): Promise<UserListResponse> {
  const headers: any = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const queryParams = new URLSearchParams();
  if (params?.role) queryParams.append('role', params.role);
  if (params?.email) queryParams.append('email', params.email);

  const url = queryParams.toString() ? `${apiUrl}?${queryParams.toString()}` : apiUrl;

  const response = await axios.get(url, { headers });
  return response.data;
}

const appendUsersSimpleFilterParams = (
  queryParams: URLSearchParams,
  params: Pick<
    UserListParams,
    | 'city'
    | 'gender'
    | 'program'
    | 'pod'
    | 'tag'
    | 'group'
    | 'is_app_unlocked'
    | 'enrollment_active'
    | 'pending'
  >
) => {
  if (params.city) queryParams.append('city', params.city);
  if (params.gender) queryParams.append('gender', params.gender);
  if (params.program) queryParams.append('program', params.program);
  if (params.pod) queryParams.append('pod', params.pod);
  if (params.tag) queryParams.append('tag', params.tag);
  if (params.group) queryParams.append('group', params.group);
  if (params.is_app_unlocked) queryParams.append('is_app_unlocked', params.is_app_unlocked);
  if (params.enrollment_active) queryParams.append('enrollment_active', params.enrollment_active);
  if (params.pending) queryParams.append('pending', params.pending);
};

export async function getUsersSimplePaginated(
  params: UserListParams,
  accessToken?: string,
  config?: { signal?: AbortSignal }
): Promise<UserListResponse> {
  const headers: any = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const queryParams = new URLSearchParams();
  if (params.role) queryParams.append('role', params.role);
  if (params.email) queryParams.append('email', params.email);
  if (params.search) queryParams.append('search', params.search);
  if (params.search_by && params.search_by !== 'all') {
    queryParams.append('search_by', params.search_by);
  }
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.page_size) queryParams.append('page_size', params.page_size.toString());
  if (params.pinned_ids) queryParams.append('pinned_ids', params.pinned_ids);
  if (params.ordering) queryParams.append('ordering', params.ordering);
  appendUsersSimpleFilterParams(queryParams, params);

  const response = await axios.get(`${apiUrl}?${queryParams.toString()}`, {
    headers,
    signal: config?.signal,
  });
  return response.data;
}

/**
 * Fetches all users across paginated API requests and combines them into a single response.
 * Stops after MAX_PAGINATED_FETCH_PAGES (100) requests, loading at most MAX_USERS_FETCH_ALL_COUNT
 * (10,000) users. Prefer passing Hub filters as query params so the server can narrow results.
 */
export async function fetchAllUsersSimplePaginated(
  params: Omit<UserListParams, 'page' | 'page_size'>,
  accessToken?: string
): Promise<UserListResponse> {
  const pageSize = USERS_SIMPLE_FETCH_PAGE_SIZE;
  let page = 1;
  let allResults: UserSimpleResponse[] = [];
  let totalCount = 0;
  let pinned: UserSimpleResponse[] | undefined;

  while (page <= MAX_PAGINATED_FETCH_PAGES) {
    const response = await getUsersSimplePaginated(
      { ...params, page, page_size: pageSize },
      accessToken
    );
    const results = response.results ?? [];
    allResults = allResults.concat(results);
    totalCount = response.count ?? allResults.length;
    if (page === 1 && response.pinned?.length) {
      pinned = response.pinned;
    }

    const totalPages = response.total_pages ?? Math.ceil(totalCount / pageSize);
    if (
      !shouldFetchAnotherUsersSimplePage({
        page,
        totalCount,
        totalPages,
        resultsLength: results.length,
        pageSize,
      })
    ) {
      break;
    }
    page += 1;
  }

  const truncated = allResults.length < totalCount;

  if (truncated) {
    logger.warn(
      `fetchAllUsersSimplePaginated truncated at ${allResults.length}/${totalCount} users (max ${MAX_USERS_FETCH_ALL_COUNT})`
    );
  }

  return {
    count: totalCount,
    results: allResults,
    pinned,
    page: 1,
    page_size: allResults.length,
    total_pages: 1,
    truncated,
    max_fetchable_users: MAX_USERS_FETCH_ALL_COUNT,
  };
}

/**
 * Look up users by their emails. Returns a map of email -> user.
 * Used for CSV bulk operations where we need to find users not on the current page.
 */
export async function getUsersByEmails(
  emails: string[],
  accessToken?: string
): Promise<Map<string, UserSimpleResponse>> {
  const headers: any = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const results = new Map<string, UserSimpleResponse>();

  const lookupPromises = emails.map(async (email) => {
    try {
      const response = await axios.get(`${apiUrl}?email=${encodeURIComponent(email.trim())}`, {
        headers,
      });
      const users = response.data?.results || [];
      if (users.length > 0) {
        results.set(email.toLowerCase(), users[0]);
      }
    } catch (error) {
      logger.warn(`Failed to lookup user by email: ${email}`, error);
    }
  });

  await Promise.all(lookupPromises);
  return results;
}

// Export types for use in components
export type {
  UserSimpleResponse,
  UserProfileData,
  UserListResponse,
  UserListParams,
  ProfileWaiting,
};
